import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "page-container calendar-page-container relative" }
const _hoisted_2 = { class: "page-header page-header-with-tabs" }
const _hoisted_3 = { class: "font-normal" }
const _hoisted_4 = { class: "page-header-tab-bar flex align-items-center" }
const _hoisted_5 = { class: "page-header-tab-bar-right" }
const _hoisted_6 = { class: "filters-menu" }
const _hoisted_7 = { class: "filters-menu-item" }
const _hoisted_8 = { class: "filters-menu-item" }
const _hoisted_9 = { class: "filters-menu-item" }
const _hoisted_10 = { class: "filters-menu-item" }
const _hoisted_11 = { class: "filters-menu-item" }
const _hoisted_12 = {
  key: 1,
  class: "w-full h-full absolute z-5 flex align-items-center justify-content-center pointer-events-none"
}

import { computed, onMounted, ref, onUnmounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import FullCalendarComponent from '@fullcalendar/vue3';
import { DatePointApi, EventClickArg } from '@fullcalendar/core';
import AutoComplete, { AutoCompleteCompleteEvent } from 'primevue/autocomplete';
import OverlayPanel from 'primevue/overlaypanel';
import TreeSelect from 'primevue/treeselect';
import AppTabMenu from '@/components/AppTabMenu';
import DocumentedHours from './components/DocumentedHours.vue';
import EventsModals from '@/components/EventsModals';
import { useAuthStore, useCalendarStore, useEventsModalsStore } from '@/store';
import { can, usePersonTypes, useRequestCancel, useRouting } from '@/composables';
import { PERMISSIONS } from '@/enum';
import { FiltersTherapyEventsInterface } from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'Calendar',
  setup(__props) {

const authStore = useAuthStore();
const { isSwitchTenant } = storeToRefs(authStore);

const calendarStore = useCalendarStore();
const eventsModalsStore = useEventsModalsStore();
const { filters, documentedHoursDateRange } = storeToRefs(calendarStore);
const calendarView = ref<typeof FullCalendarComponent>();

const activeIndex = ref<number>(0);
const router = useRouter();
const selectedDateEnd = ref<Date>(new Date());
const selectedDateStart = ref<Date>(new Date());
const selectedMonth = ref<string>(selectedDateEnd.value.toLocaleString('default', { month: 'long' }));
const selectedYear = ref<number>(selectedDateEnd.value.getFullYear());
const itemsUrl = ['/calendar', '/calendar/day', '/calendar/week', '/calendar/month'];
const items = [
  {
    label: 'List',
  },
  {
    label: 'Day',
  },
  {
    label: 'Week',
  },
  {
    label: 'Month',
  },
];

const route = useRoute();
const filterRef = ref();
const $routing = useRouting();
const attendeeFilter = ref<number | null>();
const filterButtonText = computed<string>(() => {
  const count = calendarStore.filtersAppliedCount;
  return !count ? 'Filters' : `Filters (${count})`;
});
const isClinician = usePersonTypes.isClinician();

const isSupportAdminOrSchoolAdmin = usePersonTypes.isSupportAdminOrSchoolAdmin();
const isSuperAdmin = usePersonTypes.isSuperAdmin();

onMounted(async () => {
  setActiveIndex();
  await clearAndMaybeRefetch();
  if (can([PERMISSIONS.THERAPY_EVENT_INDEX_FILTERS])) {
    await calendarStore.getServices();
    await calendarStore.getEventStatuses();
    await calendarStore.getDisciplines();
    await calendarStore.getOrganizations();
  }

  if (!calendarStore.redirectedToDate && calendarView.value?.calendar.getApi) {
    navigateToToday();
  } else {
    if (calendarView.value?.calendar.getApi) {
      calendarView.value?.calendar.getApi().changeView('timeGridDay', calendarStore.getRedirectedDate());
    }
    if (calendarStore.evenToOpen) {
      eventsModalsStore.openEventModalById(calendarStore.evenToOpen);
    }
  }
});

onBeforeUnmount(() => {
  calendarStore.resetEvents();
  useRequestCancel().requestSaverAbortAll();
  calendarView.value?.calendar.getApi().removeAllEvents();
});

onUnmounted(() => calendarView.value?.calendar.getApi().destroy());

const setActiveIndex = () => {
  itemsUrl.forEach((url, index) => {
    if (route.path.includes(url)) activeIndex.value = index;
  });
};

const updateDayDate = (date: string, month: string, year: number) => {
  calendarView.value?.calendar.getApi().gotoDate(date);
  selectedMonth.value = month;
  selectedYear.value = year;
  activeIndex.value = 1;
};

const toggleFilters = (event: Event) => filterRef.value.toggle(event);

// Disabled due to https://elumatherapy.atlassian.net/browse/TMS-860
// const settings = ref();
// const toggleSettings = (event: Event) => settings.value.toggle(event);

const createNewEventFromCalendar = (info: DatePointApi) => {
  if (can([PERMISSIONS.THERAPY_EVENT_STORE])) {
    eventsModalsStore.preSelectedEventStartAt = info.date;
    eventsModalsStore.createNewEvent();
  }
};

const navigateToToday = () => {
  calendarView.value?.calendar?.getApi()?.today();
  updateCalendar();
};

const navigatePrev = () => {
  calendarView.value?.calendar.getApi().prev();
  updateCalendar();
};

const navigateNext = () => {
  calendarView.value?.calendar.getApi().next();
  updateCalendar();
};

const updateCalendar = () => {
  updateSelectedDate();
  getEvents();
};

const updateSelectedDate = () => {
  calendarStore.currentCalendarView = calendarView.value?.calendar.getApi().view.type;
  const start = calendarView.value?.calendar.getApi().currentData.dateProfile.activeRange.start;
  const end = calendarView.value?.calendar.getApi().currentData.dateProfile.activeRange.end;
  selectedDateStart.value = start;
  selectedDateEnd.value = end;

  selectedMonth.value = calendarView.value?.calendar
    .getApi()
    .currentData.dateProfile.currentRange.end.toLocaleString('default', { month: 'long' });
  selectedYear.value = calendarView.value?.calendar.getApi().currentData.dateProfile.currentRange.end.getFullYear();
  setDocumentedHoursDateRange();
};

// To show an accurate amount for the documented hours, we need to set an alternate date range for
// the documented hours getter to use
const setDocumentedHoursDateRange = () => {
  //if month view
  if (activeIndex.value === 3) {
    let date = new Date(calendarView.value?.calendar.getApi().currentData.dateProfile.currentRange.end);
    date = new Date(date.getFullYear(), date.getMonth(), 1);

    documentedHoursDateRange.value[0] = date.toLocaleDateString('fr-CA').split(',')[0];
    documentedHoursDateRange.value[1] = calendarView.value?.calendar
      .getApi()
      .currentData.dateProfile.currentRange.end.toLocaleDateString('fr-CA')
      .split(',')[0];
  }
};

const organizationAssociated = computed<number[]>(
  () => authStore.organizationAssociated.map((organization) => organization.key) as number[],
);

/**
 * call the api  to get events for the current start/end span
 */
const getEvents = async () => {
  const start = selectedDateStart.value.toLocaleDateString('fr-CA').split(',');
  const end = selectedDateEnd.value.toLocaleDateString('fr-CA').split(',');
  attendeeFilter.value = Number($routing.route.query.attendee);

  const filtersEvents = attendeeFilter.value
    ? {
        start_at: start[0],
        end_at: end[0],
        attendee_id: attendeeFilter.value,
      }
    : {
        start_at: start[0],
        end_at: end[0],
      };

  const filtersEventsToEvaluate: FiltersTherapyEventsInterface = can([PERMISSIONS.THERAPY_EVENT_INDEX_ALL])
    ? { ...filtersEvents }
    : {
        ...filtersEvents,
        organizations: organizationAssociated.value,
      };

  await calendarStore.getEvents(filtersEventsToEvaluate);
};

const onDayView = (date: Date) => {
  calendarStore.redirectedToDate = date;
  router.push(`/calendar/day`);
  activeIndex.value = 1;
};

const handleTabMenuSelect = (index: number) => {
  activeIndex.value = index;
  const attendeeFilterUrl = attendeeFilter.value ? `?attendee=${attendeeFilter.value}` : '';
  const url = `${itemsUrl[index]}${attendeeFilterUrl}`;
  router.push(url);
};

const onDocumentEvent = async (info: EventClickArg) => {
  eventsModalsStore.onDocumentEvent(info.event);
};

const viewMounted = () => updateCalendar();

const onEditEvent = async (info: EventClickArg) => {
  const isCaseloadManagement = calendarStore.isCaseloadManagement(info.event._def.extendedProps.eventType);

  if (isSupportAdminOrSchoolAdmin || isSuperAdmin) {
    eventsModalsStore.onDocumentEvent(info.event);
    return;
  }

  if (isCaseloadManagement) {
    eventsModalsStore.onEditEvent(info.event);
    return;
  }

  if (info.event.classNames.includes('documented')) {
    eventsModalsStore.onDocumentEvent(info.event);
    return;
  }

  eventsModalsStore.onEditEvent(info.event);
};

const clearAndMaybeRefetch = async () => await calendarStore.setDefaultFilters();

const searchEvents = (event: AutoCompleteCompleteEvent) => calendarStore.searchStudentSuggestions(event.query.trim());

return (_ctx: any,_cache: any) => {
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_Dropdown = _resolveComponent("Dropdown")!
  const _component_Loader = _resolveComponent("Loader")!
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h1", _hoisted_3, [
        _createElementVNode("strong", null, _toDisplayString(selectedMonth.value), 1),
        _createTextVNode(" " + _toDisplayString(selectedYear.value), 1)
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(_unref(AppTabMenu), {
          items: items,
          "active-index": activeIndex.value,
          onTab: handleTabMenuSelect
        }, null, 8, ["active-index"]),
        _createVNode(_component_AppButton, {
          id: "calendar-today",
          type: "secondary",
          text: "Today",
          onClick: navigateToToday
        }),
        _createVNode(_component_AppButton, {
          id: "calendar-prev",
          class: "calendar-arrow-button",
          type: "secondary",
          "icon-left": "chevron_left",
          text: "",
          onClick: navigatePrev
        }),
        _createVNode(_component_AppButton, {
          id: "calendar-next",
          class: "calendar-arrow-button",
          type: "secondary",
          "icon-left": "chevron_right",
          text: "",
          onClick: navigateNext
        }),
        (_unref(isClinician))
          ? (_openBlock(), _createBlock(DocumentedHours, {
              key: 0,
              class: "ml-2"
            }))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(_unref(AutoComplete), {
            modelValue: _unref(filters).students.selected,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(filters).students.selected) = $event)),
            multiple: true,
            placeholder: "Search Attendees",
            suggestions: _unref(filters).students.options,
            onChange: _unref(calendarStore).filterEvents,
            onComplete: _cache[1] || (_cache[1] = ($event: any) => (searchEvents($event)))
          }, null, 8, ["modelValue", "suggestions", "onChange"]),
          (_unref(can)([_unref(PERMISSIONS).THERAPY_EVENT_INDEX_FILTERS]))
            ? (_openBlock(), _createBlock(_component_AppButton, {
                key: 0,
                type: "secondary",
                text: filterButtonText.value,
                "icon-left": "filter_list",
                onClick: toggleFilters
              }, null, 8, ["text"]))
            : _createCommentVNode("", true),
          (_unref(can)([_unref(PERMISSIONS).THERAPY_EVENT_INDEX_FILTERS]))
            ? (_openBlock(), _createBlock(_unref(OverlayPanel), {
                key: 1,
                ref_key: "filterRef",
                ref: filterRef
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_6, [
                    _createElementVNode("div", _hoisted_7, [
                      _cache[7] || (_cache[7] = _createElementVNode("p", null, "Event Type", -1)),
                      _createVNode(_component_Dropdown, {
                        modelValue: _unref(filters).eventType.selected,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(filters).eventType.selected) = $event)),
                        options: _unref(filters).eventType.options,
                        loading: _unref(filters).eventType.loading,
                        onChange: _unref(calendarStore).filterEvents
                      }, null, 8, ["modelValue", "options", "loading", "onChange"])
                    ]),
                    _createElementVNode("div", _hoisted_8, [
                      _cache[8] || (_cache[8] = _createElementVNode("p", null, "Discipline", -1)),
                      _createVNode(_component_Dropdown, {
                        modelValue: _unref(filters).discipline.selected,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(filters).discipline.selected) = $event)),
                        options: _unref(filters).discipline.options,
                        loading: _unref(filters).discipline.loading,
                        "option-label": "name",
                        "option-value": "value",
                        onChange: _unref(calendarStore).filterEvents
                      }, null, 8, ["modelValue", "options", "loading", "onChange"])
                    ]),
                    _createElementVNode("div", _hoisted_9, [
                      _cache[9] || (_cache[9] = _createElementVNode("p", null, "Status", -1)),
                      _createVNode(_component_Dropdown, {
                        modelValue: _unref(filters).eventStatus.selected,
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(filters).eventStatus.selected) = $event)),
                        options: _unref(filters).eventStatus.options,
                        onChange: _unref(calendarStore).filterEvents
                      }, null, 8, ["modelValue", "options", "onChange"])
                    ]),
                    _createElementVNode("div", _hoisted_10, [
                      _cache[10] || (_cache[10] = _createElementVNode("p", null, "Organization", -1)),
                      _createVNode(_unref(TreeSelect), {
                        id: "parent_organization",
                        modelValue: _unref(filters).organization.selected,
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((_unref(filters).organization.selected) = $event)),
                        options: _unref(filters).organization.options,
                        loading: _unref(filters).organization.loading,
                        "selection-mode": "multiple",
                        display: "chip",
                        "option-label": "label",
                        "option-value": "key",
                        placeholder: "All Organizations",
                        class: "w-full font-bold",
                        "show-toggle-all": false,
                        "scroll-height": "200px",
                        "meta-key-selection": false,
                        onChange: _unref(calendarStore).filterEvents
                      }, null, 8, ["modelValue", "options", "loading", "onChange"])
                    ]),
                    _createElementVNode("div", _hoisted_11, [
                      (_unref(calendarStore).isFilterSet)
                        ? (_openBlock(), _createBlock(_component_AppButton, {
                            key: 0,
                            type: "secondary",
                            text: "Clear All",
                            onClick: clearAndMaybeRefetch
                          }))
                        : _createCommentVNode("", true)
                    ])
                  ])
                ]),
                _: 1
              }, 512))
            : _createCommentVNode("", true),
          (_unref(can)([_unref(PERMISSIONS).THERAPY_EVENT_STORE]))
            ? (_openBlock(), _createBlock(_component_AppButton, {
                key: 2,
                type: "primary",
                text: "New Event",
                "icon-left": "add",
                onClick: _cache[6] || (_cache[6] = () => _unref(eventsModalsStore).createNewEvent())
              }))
            : _createCommentVNode("", true),
          _createVNode(_unref(EventsModals))
        ])
      ])
    ]),
    (_unref(calendarStore).loading)
      ? (_openBlock(), _createBlock(_component_Loader, { key: 0 }))
      : _createCommentVNode("", true),
    (!_unref(calendarStore).loading && !_unref(calendarStore).events.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_12, [...(_cache[11] || (_cache[11] = [
          _createElementVNode("p", { class: "surface-100 border-round-lg p-2" }, "No Events in current date range", -1)
        ]))]))
      : _createCommentVNode("", true),
    _createVNode(_component_router_view, null, {
      default: _withCtx(({ Component }) => [
        _createElementVNode("div", {
          class: _normalizeClass({ banner: _unref(isSwitchTenant) })
        }, [
          (_openBlock(), _createBlock(_resolveDynamicComponent(Component), {
            ref_key: "calendarView",
            ref: calendarView,
            onDocumentEvent: onDocumentEvent,
            onEditEvent: onEditEvent,
            onNewEvent: createNewEventFromCalendar,
            onDayView: onDayView,
            onUpdateDay: updateDayDate,
            onMounted: viewMounted
          }, null, 544))
        ], 2)
      ]),
      _: 1
    })
  ]))
}
}

})